(() => {
  if (customElements.get('atoto-hero-carousel')) return;
  class AtotoHeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (target, type, fn, options = {}) => target.addEventListener(type, fn, {...options, signal: this.abort.signal});
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-hero-index]')];
      this.index = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileViewport = matchMedia('(max-width: 599.98px)');
      this.paused = this.dataset.autoplay !== 'true' || this.reducedMotion.matches;
      this.visible = true;
      this.hovered = false;
      this.focused = false;
      this.editorSelected = false;
      on(this, 'click', event => {
        const control = event.target.closest('[data-hero-action], [data-hero-index]');
        if (!control || !this.contains(control)) return;
        if (control.dataset.heroIndex !== undefined) this.show(Number(control.dataset.heroIndex));
        else if (control.dataset.heroAction === 'prev') this.show(this.index - 1);
        else if (control.dataset.heroAction === 'next') this.show(this.index + 1);
        else { this.paused = !this.paused; this.updatePlayback(); }
      });
      on(this, 'keydown', event => {
        if (/INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') {this.hovered = true; this.updatePlayback();} });
      on(this, 'pointerleave', () => {this.hovered = false; this.updatePlayback();});
      on(this, 'focusin', () => {this.focused = true; this.updatePlayback();});
      on(this, 'focusout', event => {this.focused = this.contains(event.relatedTarget); this.updatePlayback();});
      on(this, 'touchstart', event => {
        const touch = event.touches[0]; this.touchStart = {x: touch.clientX, y: touch.clientY};
      }, {passive: true});
      on(this, 'touchend', event => {
        if (!this.touchStart) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touchStart.x, dy = touch.clientY - this.touchStart.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
        this.touchStart = null;
      }, {passive: true});
      on(document, 'visibilitychange', () => this.updatePlayback());
      on(this.reducedMotion, 'change', () => { if (this.reducedMotion.matches) this.paused = true; this.updatePlayback(); });
      on(this.mobileViewport, 'change', () => this.updatePlayback());
      this.querySelectorAll('iframe[data-hero-youtube]').forEach(frame => {
        on(frame, 'load', () => this.updatePlayback());
      });
      on(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId || slide.contains(event.target));
        if (index < 0) return;
        this.editorSelected = true;
        this.show(index);
      });
      on(document, 'shopify:block:deselect', event => {
        if (!this.slides.some(slide => slide.dataset.blockId === event.detail?.blockId || slide.contains(event.target))) return;
        this.editorSelected = false; this.updatePlayback();
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting; this.updatePlayback();
      }, {threshold: 0.1});
      this.observer.observe(this);
      this.show(0);
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.index;
        slide.inert = i !== this.index;
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      this.updatePlayback();
    }
    updatePlayback() {
      clearTimeout(this.timer);
      const mediaPlaying = !this.paused && this.visible && !document.hidden;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.querySelectorAll('video').forEach(video => {
          const deviceMatches = video.classList.contains('atoto-hero__video--mobile') ? this.mobileViewport.matches
            : video.classList.contains('atoto-hero__video--desktop') ? !this.mobileViewport.matches : true;
          if (active && mediaPlaying && deviceMatches) video.play()?.catch(() => {});
          else video.pause();
        });
        slide.querySelectorAll('iframe[data-hero-youtube]').forEach(frame => {
          // One iframe loads only the selected device video; changing its source stops the old one.
          const source = this.mobileViewport.matches && frame.dataset.heroYoutubeMobile
            ? frame.dataset.heroYoutubeMobile : frame.dataset.heroYoutube;
          if (!active) {
            if (frame.hasAttribute('src')) frame.removeAttribute('src');
            delete frame.dataset.heroYoutubeActiveSource;
          } else {
            if (!frame.hasAttribute('src') || frame.dataset.heroYoutubeActiveSource !== source) {
              frame.dataset.heroYoutubeActiveSource = source;
              frame.src = source.replace('autoplay=0', `autoplay=${mediaPlaying ? 1 : 0}`);
            }
            frame.contentWindow?.postMessage(JSON.stringify({event:'command', func:mediaPlaying ? 'playVideo' : 'pauseVideo', args:[]}), 'https://www.youtube-nocookie.com');
          }
        });
      });
      const pause = this.querySelector('[data-hero-action="pause"]');
      if (pause) {
        pause.textContent = this.paused ? 'Play' : 'Pause';
        pause.setAttribute('aria-label', `${this.paused ? 'Play' : 'Pause'} slideshow`);
      }
      if (this.slides.length > 1 && mediaPlaying && !this.hovered && !this.focused && !this.editorSelected) {
        this.timer = setTimeout(() => this.show(this.index + 1), Number(this.dataset.speed || 5) * 1000);
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
      this.querySelectorAll('video').forEach(video => video.pause());
      this.querySelectorAll('iframe[data-hero-youtube]').forEach(frame => frame.removeAttribute('src'));
    }
  }
  customElements.define('atoto-hero-carousel', AtotoHeroCarousel);
})();
